<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="mt-2 text-3xl text-teal font-semibold leading-normal">
          A-Z of Suppliers
        </h1>
      </div>
    </div>

    <loader v-if="!loaded" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to view supplier listings.</p>
    </panel>

    <div v-else class="rounded-lg bg-white relative flex flex-col p-6 overflow-x-auto">
      <p class="mt-4 mb-8 leading-normal">This tool is where you can find all of the Suppliers partnered with The Guild, offering exclusive discounts and benefits for our Members. The A-Z of Suppliers helps you save money and time for you and your clients.</p>

      <div class="w-full pb-6 mb-8 flex flex-wrap -mx-2 border-b">
        <div class="w-full md:w-1/4 px-2">
          <input type="text" v-model="searchParams.q" placeholder="Search" class="w-full rounded bg-white p-3 border" />
        </div>

        <div class="w-full md:w-1/4 px-2">
          <select v-model="searchParams.category_id" class="w-full rounded bg-white p-3 border">
            <option :value="null">[All Categories]</option>
            <option v-for="category in categoryOptions" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </div>

        <div class="w-full md:w-1/4 px-2">
          <select v-model="searchParams.service_id" class="w-full rounded bg-white p-3 border">
            <option :value="null">[All Services]</option>
            <option v-for="service in serviceOptions" :key="service.id" :value="service.id">
              {{ service.name }}
            </option>
          </select>
        </div>
      </div>

      <table class="w-full mb-8 leading-normal">
        <tbody>
          <tr v-for="(supplier, i) in suppliers" :key="i" class="border-b border-grey-light text-sm align-top">
            <td class="p-4 w-1/6"><router-link :to="`/suppliers/${supplier.id}`" class="text-teal no-underline hover:underline">
              <img :src="supplier.logo_url" :alt="supplier.name" class="w-48 overflow-hidden border mb-2 mr-8" /></router-link></td>
            <td class="p-4 w-1/2">
              <div class="border-r min-h-32">
                <h3><router-link :to="`/suppliers/${supplier.id}`" class="text-teal no-underline hover:underline">{{ supplier.name }}</router-link></h3>
                <div v-html="supplier.summary" class="pr-4"></div>
                <router-link :to="`/suppliers/${supplier.id}`" class="text-teal no-underline hover:underline">Tell me more &gt;&gt;</router-link>
              </div>
            </td>
            <td class="p-4 w-1/6">
              <h4>Category</h4>
              {{ supplier.categories.map((category) => { return category.name }).join(', ') }}
              <h4 class="mt-2">Services</h4>
              {{ supplier.services.map((service) => { return service.name }).join(', ') }}
            </td>
            <td class="p-4 w-1/6">
              <star-rating-component :fixed=true :value="supplier.admin_rating_override ? supplier.admin_rating : supplier.star_rating" />
              <div class="text-sm mb-2" v-if="!supplier.admin_rating_override"><strong class="font-semibold">{{ supplier.star_rating }}</strong> stars - {{ supplier.ratings_count }} {{ supplier.ratings_count === 1 ? 'review' : 'reviews' }}</div>
              <div v-if="highestDiscount(supplier).value > 0 && supplier.discount_section_type == 1">
                <h4>Discounts</h4>
                <div class="flex items-center">
                  <icon name="badge-percent-light-teal" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_standard.discount_value && supplier.discounts.membership_standard.discount_type_id == 2"></icon>
                  <icon name="badge-percent-light-red" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_enhanced.discount_value && supplier.discounts.membership_enhanced.discount_type_id == 2"></icon>
                  <icon name="badge-percent-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 2"></icon>
                  <icon name="sterling-sign-light-teal" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_standard.discount_value && supplier.discounts.membership_standard.discount_type_id == 1"></icon>
                  <icon name="sterling-sign-light-red" class="icon w-5 h-1 mr-1" :center="false" v-if="supplier.discounts.membership_enhanced.discount_value && supplier.discounts.membership_enhanced.discount_type_id == 1"></icon>
                  <icon name="sterling-sign-light-purple" class="icon w-5 h-1 mr-2" :center="false" v-if="supplier.discounts.membership_premium.discount_value && supplier.discounts.membership_premium.discount_type_id == 1"></icon>
                  Up to {{ formatDiscount(highestDiscount(supplier).value, highestDiscount(supplier).typeId) }} off
                </div>
              </div>
              <div v-else-if="supplier.discount_section_type == 1">
                <h4>Discounts</h4>
                <div class="flex items-center" v-if="!supplier.key_points">
                  <router-link :to="`/suppliers/${supplier.id}`" class="text-grey-dark no-underline hover:underline">Find out more ...</router-link>
                </div>
              </div>
              <div v-else-if="supplier.discount_section_type == 2">
                <h4>Partnership Benefits</h4>
                <div class="flex items-center" v-if="!supplier.key_points">
                  <router-link :to="`/suppliers/${supplier.id}`" class="text-grey-dark no-underline hover:underline">Find out more ...</router-link>
                </div>
              </div>
              <div class="flex items-center" v-if="supplier.key_points">
                <router-link :to="`/suppliers/${supplier.id}`" class="text-grey-dark no-underline hover:underline">{{ supplier.key_points }}</router-link>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <paginator :data="pagination" @change="pageChanged" />
    </div>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { isEmpty } from 'lodash'
import { mapState, mapActions } from 'vuex'
import StarRatingComponent from '../../components/forms/StarRatingComponent.vue'
export default {
  name: 'SuppliersIndex',
  components: { StarRatingComponent },
  data () {
    return {
      loaded: false,
      suppliers: [],
      searchParams: {
        q: null,
        network_id: null,
        category_id: null,
        service_id: null,
        page: 1,
        trashed: 0
      },
      categoryOptions: [],
      serviceOptions: [],
      pagination: {
        current_page: 1,
        last_page: null
      }
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    officeAccess: function () {
      // Access is currently restricted to Guild network only
      if (this.selectedOffice && this.selectedOffice.network_id === 2) {
        return true
      }

      return false
    }
  },
  watch: {
    selectedOffice: {
      handler () {
        this.loadPage()
      },
      deep: true
    },
    searchParams: {
      deep: true,
      handler () {
        this.search()
      }
    }
  },
  created () {
    this.searchParams.network_id = this.selectedOffice.network_id
    this.loadPage()
  },
  methods: {
    isEmpty,
    ...mapActions([
      'displayToastNotification'
    ]),
    loadPage () {
      this.loaded = false
      Promise.all([
        this.search(),
        this.loadCategories(),
        this.loadServices()
      ]).then(() => {
        this.loaded = true
      })
    },
    search () {
      return v2Api.get('suppliers', {
        params: this.searchParams
      }).then((response) => {
        this.suppliers = response.data.data
        this.pagination = response.data
      }).catch(() => {
        this.loaded = true
      })
    },
    loadCategories () {
      v2Api.get('supplier-categories').then((response) => {
        this.categoryOptions = response.data
      })
    },
    loadServices () {
      v2Api.get('supplier-services').then((response) => {
        this.serviceOptions = response.data
      })
    },
    viewDetails (supplier) {
      this.$router.push(`/suppliers/${supplier.id}`)
    },
    pageChanged (page) {
      this.searchParams.page = page
    },
    openUrl (url) {
      window.open(url, '_blank')
    },
    highestDiscount (supplier) {
      let highestDiscount = {
        value: 0,
        typeId: null
      }

      if (!supplier) {
        return highestDiscount
      }

      for (let key in supplier.discounts) {
        if ((supplier.discounts[key].discount_value || 0) > highestDiscount.value) {
          highestDiscount = {
            value: supplier.discounts[key].discount_value,
            typeId: supplier.discounts[key].discount_type_id
          }
        }
      }

      return highestDiscount
    },
    formatDiscount (value, typeId) {
      if (typeId === 2) {
        return value + '%'
      }

      return '£' + value
    }
  }
}
</script>
